import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy, Mail, Lock } from "lucide-react";

const TONES = [
  { key: "professional", label: "Professional" },
  { key: "warm", label: "Warm" },
  { key: "bold", label: "Bold" },
  { key: "concise", label: "Concise" },
];

const PRESETS = [
  { key: "standard", label: "Standard", desc: "Classic 3–4 paragraph cover letter." },
  { key: "formal", label: "Formal", desc: "Structured 'Dear Hiring Manager…' — best for law, gov, healthcare, banking." },
  { key: "startup", label: "Startup", desc: "Punchy first-person hook, product-led, tight." },
  { key: "recruiter_outreach", label: "Recruiter DM", desc: "Cold-outreach style: short, conversational, asks for a chat." },
];

export default function CoverLetter() {
  const { user } = useAuth();
  const loc = useLocation();
  const preJob = loc.state?.job;
  const [resume, setResume] = useState("");
  const [jobDesc, setJobDesc] = useState(preJob?.description ? preJob.description.replace(/<[^>]+>/g, " ") : "");
  const [company, setCompany] = useState(preJob?.company || "");
  const [jobTitle, setJobTitle] = useState(preJob?.title || "");
  const [tone, setTone] = useState("professional");
  const [preset, setPreset] = useState("standard");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const isPro = user?.plan === "pro";

  useEffect(() => {
    if (!user) return;
    api.get("/resume").then(({ data }) => { if (data.text) setResume(data.text); }).catch(() => {});
  }, [user]);

  const run = async () => {
    if (!isPro) { toast.error("Upgrade to Pro to generate cover letters"); return; }
    if (!resume.trim() || !jobDesc.trim() || !company.trim()) { toast.error("Add resume, company, and job description"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/ai/cover-letter", { resume, job_description: jobDesc, company, job_title: jobTitle, tone, preset });
      setResult(data);
      toast.success(`Cover letter ready · ${preset === "standard" ? tone : preset} · ${tone}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI request failed");
    } finally { setBusy(false); }
  };

  const copyLetter = () => {
    if (!result?.cover_letter) return;
    navigator.clipboard.writeText(result.cover_letter);
    toast.success("Copied");
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="label-eyebrow">AI Cover Letter</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Write a cover letter in seconds</h1>
          <p className="mt-2 text-gray-600 max-w-2xl">Grounded in your actual resume. Never invents facts.</p>
        </div>
        {!isPro && (
          <Link to="/pricing" data-testid="cover-upgrade-cta" className="nb-btn bg-[#ec4899] text-white px-5 py-2.5 inline-flex items-center gap-2">
            <Lock className="w-4 h-4" /> Unlock with Pro
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="nb-card p-6">
          <h2 className="text-xl font-bold">Inputs</h2>
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="cover-company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company"
                className="border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca]" />
              <input data-testid="cover-title" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="Job title"
                className="border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca]" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest">Format preset</label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {PRESETS.map((p) => (
                  <button key={p.key} data-testid={`preset-${p.key}`} onClick={() => setPreset(p.key)} title={p.desc}
                    className={`text-left border-2 border-black rounded-xl p-2.5 transition-transform ${preset === p.key ? "bg-[#4338ca] text-white shadow-[3px_3px_0_0_#0A0A0A] -translate-y-0.5" : "bg-white hover:-translate-y-0.5"}`}>
                    <div className="font-black text-sm">{p.label}</div>
                    <div className="text-[11px] mt-0.5 opacity-80 leading-snug">{p.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest">Tone</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {TONES.map((t) => (
                  <button key={t.key} data-testid={`tone-${t.key}`} onClick={() => setTone(t.key)}
                    className={`nb-pill ${tone === t.key ? "bg-[#4338ca] text-white" : "bg-white"}`}>{t.label}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest">Your resume</label>
              <textarea data-testid="cover-resume" value={resume} onChange={(e) => setResume(e.target.value)} rows={8}
                className="mt-1 w-full border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca] font-mono text-sm"
                placeholder="Paste resume — or save one on the Tailor page to autoload here." />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest">Job description</label>
              <textarea data-testid="cover-jd" value={jobDesc} onChange={(e) => setJobDesc(e.target.value)} rows={8}
                className="mt-1 w-full border-2 border-black rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#4338ca] font-mono text-sm" />
            </div>
            <button data-testid="cover-run" onClick={run} disabled={busy}
              className="nb-btn bg-[#4338ca] text-white px-5 py-3 w-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Writing…</> : <><Sparkles className="w-4 h-4" /> Generate cover letter</>}
            </button>
          </div>
        </div>

        <div className="nb-card p-6" data-testid="cover-result">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Result</h2>
            {result && <button data-testid="cover-copy" onClick={copyLetter} className="nb-btn bg-white px-3 py-1.5 text-sm inline-flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Copy</button>}
          </div>
          {!result ? (
            <div className="mt-10 text-center text-gray-500">
              <Mail className="w-10 h-10 mx-auto text-[#ec4899]" />
              <p className="mt-3 font-bold">Your cover letter will appear here.</p>
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              {result.subject_line && (
                <div>
                  <div className="label-eyebrow">Subject line</div>
                  <p className="mt-1 font-bold">{result.subject_line}</p>
                </div>
              )}
              <div>
                <div className="label-eyebrow">Cover letter</div>
                <pre className="mt-2 whitespace-pre-wrap font-sans text-sm leading-relaxed bg-[#FEFCE8] p-4 border-2 border-black rounded-lg">{result.cover_letter}</pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
